/**
 * ScheduleGantt.tsx
 * Phase 4, Item 1 — Thin Highcharts Gantt wrapper.
 * Phase 4, Item 2 — Pattern-fill module registered; tentative bars hatched.
 * Phase 4, Item 3 — "Today" plotLine (blue) + Critical Path plotLines (red dashed).
 * Phase 4, Item 4 — Conflict glyphs: dataLabels on bars where custom.conflictType
 *                    is 'hard' or 'soft'. Hard = red "▲", Soft = orange "▲".
 *                    Click on any bar with conflictType != null navigates to
 *                    Machine Schedule via the onPointClick prop.
 *
 * Highcharts Gantt license: This project has a licensed copy of Highcharts
 * (see package.json: "highcharts" + "highcharts-react-official").
 * Per CLAUDE.md locked decisions, Highcharts Gantt is the chosen library.
 *
 * Props:
 *   series          — Highcharts series data (from masterGanttRowBuilder + tagConflicts)
 *   startDate       — ISO date 'YYYY-MM-DD' for xAxis min
 *   endDate         — ISO date 'YYYY-MM-DD' for xAxis max
 *   onPointClick    — optional callback when user clicks a bar
 *   conflictColors  — { hard, soft } theme token strings for glyph colors
 *
 * Highcharts treegrid (yAxis.type='treegrid') provides expand/collapse carets
 * for each tier row automatically.
 *
 * Pattern fills: Highcharts pattern-fill module is registered once on import.
 * Tentative bars receive a HighchartsPatternFill object on their `color`
 * property (built in ganttFillResolver.ts). No hex literals in this file.
 * Row sizing / in-chart scrolling (BUG-2026-0194) lives in ganttRowHeightSync.ts.
 */

import React, { useEffect, useMemo, useRef } from 'react';
import HighchartsReact from 'highcharts-react-official';
import Highcharts from 'highcharts/highstock';
import HighchartsGantt from 'highcharts/modules/gantt';
import HighchartsPatternFill from 'highcharts/modules/pattern-fill';
import HighchartsExporting from 'highcharts/modules/exporting';
import HighchartsOfflineExporting from 'highcharts/modules/offline-exporting';
import { Box, Typography } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import type { RefObject } from 'react';
import type { GanttSeries, CriticalPathEntry } from './masterGanttRowBuilder.js';
import { useGanttImperativeSync, type UserExtremes } from './useGanttImperativeSync.js';
import { buildGanttTooltip } from './scheduleGanttTooltip.js';
import { useScheduleHolidays } from '../../hooks/useScheduleHolidays.js';
import { buildNonWorkPlotBands } from './scheduleGanttPlotBands.js';
import {
  createGanttRowHeightController,
  type GanttRowHeightController,
} from './ganttRowHeightSync.js';

// Initialise Highcharts modules once. Defensive pattern: Highcharts 12 module
// default exports vary — sometimes a function, sometimes an already-initialised
// object. Calling a non-function default throws at module-eval time and
// blanks the whole app. Mirrors the pattern in PsdSections.tsx.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const initIfFunction = (mod: unknown): void => {
  const fn = mod as (hc: typeof Highcharts) => unknown;
  if (typeof fn === 'function') fn(Highcharts);
};
initIfFunction(HighchartsGantt);
initIfFunction(HighchartsPatternFill);
// Phase 4, Item 5 — exporting modules required for exportChart() calls.
// offline-exporting enables PDF and PNG without the Highcharts export server.
initIfFunction(HighchartsExporting);
initIfFunction(HighchartsOfflineExporting);

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

/** Color tokens for conflict glyphs — pulled from theme.schedule.* by caller. */
export interface ConflictColors {
  /** Hard conflict glyph color — theme.schedule.conflictHard */
  hard: string;
  /** Soft conflict glyph color — theme.schedule.conflictSoft */
  soft: string;
}

export interface ScheduleGanttProps {
  /** Series data produced by masterGanttRowBuilder.buildGanttChartData (+ tagConflicts) */
  series: GanttSeries[];
  /** Inclusive range start — used as xAxis.min */
  startDate: string; // 'YYYY-MM-DD'
  /** Inclusive range end — used as xAxis.max */
  endDate: string;   // 'YYYY-MM-DD'
  /**
   * Called when a Gantt bar is clicked.
   * Receives the raw Highcharts point object.
   * Phase 4 item 4: conflict bars navigate to Machine Schedule.
   * Phase 4 item 10 will add deep-links for non-conflict bars.
   */
  onPointClick?: (point: Highcharts.Point) => void;
  /**
   * Height of the chart container in pixels. Defaults to 500.
   * With `fixedRowHeight` set, this is the MAXIMUM (viewport) height instead —
   * see below.
   */
  height?: number;
  /**
   * BUG-2026-0194 — opt-in fixed row height (px). When set:
   *   - every treegrid row is exactly this tall, however many are expanded
   *     (collapsing a node makes the list shorter, not the rows taller);
   *   - the chart never grows past `height`: extra rows scroll natively INSIDE
   *     the plot area (mouse wheel works) while the date axis, navigator and
   *     horizontal scrollbar stay pinned at the top/bottom of the chart;
   *   - fewer rows than fit → the chart shrinks to the rows.
   * Omit (Quote preview modal, Employee timeline) to keep the legacy fixed-
   * height behaviour where rows stretch to fill `height`.
   */
  fixedRowHeight?: number;
  /**
   * Phase 4, Item 3 — Critical Path entries.
   * One vertical dashed-red plotLine is drawn per entry at `entry.date`.
   * Labeled with the project name.
   * Produced by computeCriticalPathDates() in masterGanttRowBuilder.
   */
  criticalPaths?: CriticalPathEntry[];
  /**
   * Phase 4, Item 4 — Colors for conflict glyphs.
   * If not provided, conflict glyphs are not rendered even if conflictType
   * is set on points. The caller (MasterGanttPage) always provides this.
   */
  conflictColors?: ConflictColors;
  /**
   * Phase 4, Item 5 — Optional ref forwarded to HighchartsReact so callers
   * can access the underlying Highcharts chart instance for export:
   *   chartRef.current?.chart.exportChart({ type: 'image/png' })
   */
  chartRef?: RefObject<HighchartsReact.RefObject | null>;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** 'YYYY-MM-DD' → Unix milliseconds (UTC midnight) for Highcharts xAxis. */
function dateToMs(iso: string): number {
  const [y, m, d] = iso.split('-').map(Number);
  return Date.UTC(y, m - 1, d);
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const ScheduleGantt: React.FC<ScheduleGanttProps> = ({
  series,
  startDate,
  endDate,
  onPointClick,
  height = 500,
  fixedRowHeight,
  criticalPaths = [],
  conflictColors,
  chartRef,
}) => {
  const theme = useTheme();

  // ── BUG-2026-0194: fixed-row-height controller (one per chart instance) ───
  // Created once; the chart's `render` event calls its onRender, and the sync
  // hook feeds it the latest viewport height. null = legacy sizing.
  const rowHeightCtlRef = useRef<GanttRowHeightController | null>(null);
  if (fixedRowHeight && !rowHeightCtlRef.current) {
    rowHeightCtlRef.current = createGanttRowHeightController(fixedRowHeight, height);
  }
  const rowHeightCtl = fixedRowHeight ? rowHeightCtlRef.current : null;

  // ── BUG-2026-0076 / BUG-2026-0093: zoom preservation + leak-free updates ──
  //
  // The chart is created ONCE (HighchartsReact `allowChartUpdate={false}`) and
  // every later change is applied imperatively by useGanttImperativeSync. This
  // is deliberate: routing data/range changes through `chart.update(options)`
  // re-initialises the treegrid yAxis, which re-registers a chart-level
  // `beforeRedraw` listener on every call with no de-duplication. Those stack
  // and multiply rendered nodes super-linearly until the tab OOMs (BUG-2026-0093).
  //
  // `userExtremesRef` captures the user's navigator/scrollbar zoom (via the
  // xAxis afterSetExtremes hook below) so the sync hook can restore it across
  // data refreshes (BUG-2026-0076).
  //
  // We keep an internal HighchartsReact ref so the sync hook always has the
  // chart instance even when the caller doesn't pass a chartRef prop; when they
  // do, we forward to it as well.
  const internalChartRef = useRef<HighchartsReact.RefObject | null>(null);
  const userExtremesRef = useRef<UserExtremes>(null);

  // Keep the latest onPointClick in a ref so the click handler baked into the
  // chart at creation time never goes stale — avoids needing a chart update
  // when the handler identity changes (e.g. multi-select mode toggling).
  const onPointClickRef = useRef(onPointClick);
  useEffect(() => {
    onPointClickRef.current = onPointClick;
  }, [onPointClick]);

  // Build Highcharts plotLines for Today + Critical Path — memoised together
  const plotLines = useMemo((): Highcharts.AxisPlotLinesOptions[] => {
    const lines: Highcharts.AxisPlotLinesOptions[] = [];

    // "Today" line — vertical blue, 2px solid, labeled "Today" at the top.
    // Stable `id` lets useGanttImperativeSync reconcile plot lines in place
    // (add/remove by id) instead of re-initialising the xAxis.
    lines.push({
      id: 'gantt-today-line',
      value: Date.now(),
      color: theme.schedule.todayLine,
      width: 2,
      zIndex: 5,
      label: {
        text: 'Today',
        align: 'left',
        rotation: 0,
        x: 4,
        y: 12,
        style: {
          color: theme.schedule.todayLine,
          fontSize: '11px',
          fontWeight: '600',
        },
      },
    });

    // Critical Path lines — one per project, red dashed at latest committed end
    for (const entry of criticalPaths) {
      lines.push({
        id: `gantt-cp-${entry.projectKey}`,
        value: entry.date,
        color: theme.palette.error.main,
        width: 2,
        dashStyle: 'Dash',
        zIndex: 4,
        label: {
          text: entry.projectName,
          align: 'left',
          rotation: 0,
          x: 4,
          y: 24,
          style: {
            color: theme.palette.error.main,
            fontSize: '10px',
            fontWeight: '500',
          },
        },
      });
    }

    return lines;
  }, [criticalPaths, theme]);

  // ── Weekend + stat-holiday shading bands ──────────────────────────────────
  // Bookings already skip non-work days in placement, but a multi-day bar still
  // visually spans Sat/Sun/holidays. These plotBands shade those columns so the
  // gap reads as "lab closed" rather than weekend work. Holidays come from the
  // server (region defaults to NB); weekends are derived from the date window.
  // Like plotLines, bands are NOT baked into the static options — they are
  // reconciled by id in useGanttImperativeSync so the axis is never re-inited.
  const { holidays } = useScheduleHolidays(startDate, endDate);
  const plotBands = useMemo(
    (): Highcharts.AxisPlotBandsOptions[] =>
      buildNonWorkPlotBands(startDate, endDate, holidays, {
        // Subtle fills so bars (which draw above, zIndex > 0) stay legible.
        weekend: alpha(theme.schedule.noActivity, 0.12),
        holiday: alpha(theme.schedule.maintenance, 0.16),
      }),
    [startDate, endDate, holidays, theme.schedule.noActivity, theme.schedule.maintenance],
  );

  // Build Highcharts options — memoised on inputs
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Highcharts point has dynamic custom props
  const options = useMemo((): Highcharts.Options => {
    const xMin = dateToMs(startDate);
    const xMax = dateToMs(endDate);

    return {
      chart: {
        type: 'gantt',
        height,
        backgroundColor: theme.palette.background.paper,
        style: {
          fontFamily: theme.typography.fontFamily as string,
        },
        // Smooth entrance + redraw transitions. Updates are applied via
        // chart.setData/redraw (see useGanttImperativeSync), which animate the
        // bars growing/sliding into place on every refresh and proposal toggle.
        animation: { duration: 650 },
        // BUG-2026-0194 (fixed rows): a scrollable plot area pins the date
        // axis, navigator and scrollbar while the rows scroll natively inside
        // the chart. `minHeight: 1` only arms the feature — the controller's
        // render hook sets the real minHeight (visible rows × row px + chrome)
        // and the chart height on every redraw. opacity 1 = the fixed axis
        // band fully hides the rows scrolling beneath it.
        ...(rowHeightCtl
          ? {
              scrollablePlotArea: { minHeight: 1, opacity: 1 },
              events: { render: rowHeightCtl.onRender },
            }
          : {}),
      },

      title: {
        text: '',  // title rendered by the page shell
      },

      credits: { enabled: false },

      // ── xAxis: date-based timeline ────────────────────────────────────────
      // BUG-2026-0077 — Daily scale only. We constrain `units` to day / week /
      // month / year (no hour or 6-hour subdivisions) and set
      // `minTickInterval` to 1 day so Highcharts never picks a sub-day tick
      // even when the user zooms in. Multi-week ranges automatically widen
      // ticks to weeks; multi-month ranges to months.
      xAxis: {
        min: xMin,
        max: xMax,
        type: 'datetime',
        dateTimeLabelFormats: {
          day:   '%e %b',
          week:  '%e %b',
          month: "%b '%y",
          year:  '%Y',
        },
        // 1 day in ms — Highcharts uses this as the smallest allowable
        // distance between ticks. Prevents 6-hour / 12-hour subdivisions.
        minTickInterval: 24 * 3600 * 1000,
        // Allowed tick granularities. Omitting hour/minute/second means
        // Highcharts will never auto-select sub-day ticks regardless of zoom.
        units: [
          ['day', [1]],
          ['week', [1]],
          ['month', [1, 3, 6]],
          ['year', [1]],
        ],
        gridLineColor: theme.border.color,
        lineColor: theme.border.color,
        tickColor: theme.border.color,
        labels: {
          style: {
            color: theme.palette.text.secondary,
            fontSize: '11px',
          },
        },
        // Phase 4, Item 3: Today line (blue) + Critical Path lines (red dashed).
        // BUG-2026-0093: plot lines are NOT set here — useGanttImperativeSync
        // adds/removes them by stable id so they update without re-initialising
        // the axis. Setting them here too would double-render them on creation.
        // BUG-2026-0076: Capture user-driven extremes so we can restore them
        // after a data refresh. `trigger` is set by Highcharts when the user
        // drags the navigator handles, scrollbar, pans, or click-drag zooms.
        // Programmatic extreme changes (options.min/max updates from React
        // re-renders, or our own setExtremes call) report no trigger or one
        // not in the user-gesture set.
        events: {
          afterSetExtremes(e: Highcharts.AxisSetExtremesEventObject): void {
            const userTriggers = new Set([
              'navigator',
              'scrollbar',
              'zoom',
              'pan',
              'rangeSelectorButton',
            ]);
            if (e.trigger && userTriggers.has(e.trigger)) {
              userExtremesRef.current = { min: e.min, max: e.max };
            }
          },
        },
      },

      // ── yAxis: treegrid for hierarchical rows ────────────────────────────
      yAxis: {
        type: 'treegrid',
        // BUG-2026-0194: Gantt defaults yAxis.staticScale to 50 and arms it
        // whenever scrollablePlotArea.minHeight is set — but it only resizes
        // while the plot is NOT scrolling, so it would fight the controller in
        // the few-rows case. Disable it; ganttRowHeightSync owns row height.
        ...(rowHeightCtl ? { staticScale: undefined } : {}),
        gridLineColor: theme.border.color,
        labels: {
          style: {
            color: theme.palette.text.primary,
            fontSize: '12px',
          },
        },
        // Expand caret icons rendered by Highcharts automatically
        treeGrid: {
          collapseButtonTheme: {
            style: {
              color: theme.palette.text.secondary,
            },
          },
        },
      },

      // ── Tooltip ───────────────────────────────────────────────────────────
      // Curated, human-readable summary pinned to the top-right corner of the
      // plot area so it never covers the hovered bar. Config lives in
      // scheduleGanttTooltip.ts (file-size cap); theme tokens passed in.
      tooltip: buildGanttTooltip({
        borderColor: theme.border.color,
        backgroundColor: theme.palette.background.paper,
        textPrimary: theme.palette.text.primary,
        textSecondary: theme.palette.text.secondary,
        textDisabled: theme.palette.text.disabled,
        conflictHard: theme.schedule.conflictHard,
        conflictSoft: theme.schedule.conflictSoft,
        fontFamily: theme.typography.fontFamily as string,
      }),

      // ── Navigator: mini-overview below chart (built into highstock base) ─
      navigator: {
        enabled: true,
        // BUG-2026-0207 (rework): never let new data drag the window to the
        // data's right edge. The stickToMin half (no option exists) is cleared
        // per update in useGanttImperativeSync — see its step 1b.
        stickToMax: false,
        series: {
          type: 'line',
          color: theme.schedule.committed,
        },
        handles: {
          backgroundColor: theme.schedule.committed,
        },
        xAxis: {
          labels: {
            style: { color: theme.palette.text.secondary },
          },
        },
      },

      scrollbar: { enabled: true },

      rangeSelector: { enabled: false },

      // ── Series ────────────────────────────────────────────────────────────
      series: series.map(s => ({
        ...s,
        type: 'gantt',
        point: {
          events: {
            // Read the latest handler from a ref so the click stays live even
            // though the chart (and this baked-in handler) is created only once.
            click: function (this: Highcharts.Point) {
              onPointClickRef.current?.(this);
            },
          },
        },
        // Highcharts supports an array of dataLabels on a series — we use two:
        //   [0] the bar name label (always shown on visible bars)
        //   [1] the conflict glyph "▲" (shown only when conflictType != null)
        dataLabels: [
          // ── Bar name label ───────────────────────────────────────────────
          {
            enabled: true,
            format: '{point.name}',
            align: 'left',
            padding: 4,
            style: {
              // White text on all bar types — readable on sage/blue/green fills.
              // Color token: use primary text-on-dark. Not a hex literal in a
              // component — this is a Highcharts dataLabels style object which
              // must accept a literal string; we pull from theme.
              color: theme.palette.common.white,
              fontSize: '11px',
              fontWeight: '400',
              textOutline: '0px',
            },
            // Only show labels on leaf rows (booking/assignment bars)
            filter: {
              property: 'shapeArgs.height',
              operator: '>',
              value: 18,
            },
          },
          // ── Conflict glyph label ─────────────────────────────────────────
          // Renders "▲" at the right end of bars with conflictType != null.
          // Hard = red (theme.schedule.conflictHard), Soft = orange (conflictSoft).
          // The formatter returns '' (empty) for non-conflicted bars so nothing is drawn.
          ...(conflictColors
            ? [{
                enabled: true,
                align: 'right' as const,
                padding: 2,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                formatter(this: any): string {
                  const ct: string | null = this.point?.custom?.conflictType ?? null;
                  if (!ct) return '';
                  // Unicode filled upward-pointing triangle — works in all browsers
                  return '▲';
                },
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                style: function(this: any): Record<string, string> {
                  const ct: string | null = this?.custom?.conflictType ?? null;
                  // Note: Highcharts evaluates style functions per point.
                  // We return a static style here; the formatter above already
                  // controls visibility — so we just pick the color based on
                  // what the formatter would show.
                  const color = ct === 'hard'
                    ? conflictColors.hard
                    : conflictColors.soft;
                  return {
                    color,
                    fontSize: '10px',
                    fontWeight: '700',
                    textOutline: '1px white',
                  };
                },
                useHTML: false,
                filter: {
                  property: 'shapeArgs.height',
                  operator: '>',
                  value: 12,
                },
              }]
            : []),
        ],
        // Override individual point colors from our data
        colorByPoint: false,
      })) as Highcharts.SeriesGanttOptions[],

      // ── Legend ────────────────────────────────────────────────────────────
      legend: { enabled: false },

      // ── Exporting ─────────────────────────────────────────────────────────
      // Phase 4, Item 5 — enabled so exportChart() works.
      // Actual button UI lives in MasterGanttToolbar; the built-in Highcharts
      // export button is hidden (buttons: []).
      exporting: {
        enabled: true,
        buttons: { contextButton: { enabled: false } },
      },
    };
  }, [series, startDate, endDate, height, theme, conflictColors, rowHeightCtl]);

  // ── BUG-2026-0093: imperative, leak-free chart updates ────────────────────
  // Drive all post-creation changes (data, range, plot lines, height) through
  // setData/setExtremes/addPlotLine/setSize instead of chart.update(options).
  // This is what stops the treegrid yAxis from being re-initialised (and the
  // beforeRedraw listeners from stacking) on every refresh/toggle. It also
  // restores the user's navigator zoom across data refreshes (BUG-2026-0076).
  useGanttImperativeSync(
    internalChartRef,
    {
      seriesData: series[0]?.data ?? [],
      xMin: dateToMs(startDate),
      xMax: dateToMs(endDate),
      plotLines,
      plotBands,
      height,
      rowHeightController: rowHeightCtl,
    },
    userExtremesRef,
  );

  // Show empty state when no data
  if (!series.length || !series[0].data.length) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height,
          color: theme.palette.text.disabled,
          gap: 1,
        }}
      >
        <Typography variant="body1">
          No scheduled work found for this date range.
        </Typography>
        <Typography variant="caption">
          Adjust the date range or add tasks via a Quote or Work Order.
        </Typography>
      </Box>
    );
  }

  // Callback ref: capture into internalChartRef and also forward to the
  // external chartRef when one was provided. This lets both useGanttImperativeSync
  // AND the caller (e.g. MasterGanttPage's exportChart) share the same
  // HighchartsReact instance.
  const handleChartRef = (instance: HighchartsReact.RefObject | null): void => {
    internalChartRef.current = instance;
    if (chartRef) {
      chartRef.current = instance;
    }
  };

  // BUG-2026-0093: `allowChartUpdate={false}` — create the chart ONCE and never
  // let the wrapper call chart.update(options). All subsequent changes go
  // through useGanttImperativeSync. This is the core of the leak fix: chart.update
  // re-inits the treegrid yAxis and stacks a beforeRedraw listener every time.
  return (
    <HighchartsReact
      highcharts={Highcharts}
      constructorType="ganttChart"
      options={options}
      allowChartUpdate={false}
      ref={handleChartRef}
    />
  );
};

export default ScheduleGantt;
